<template>
    <div class="content-wrapper">
        <div class="content" :class="{ 'content--other': isFullWidthAuth }">
            <component
                :is="activeComponent"
                v-if="activeComponent"
                :currentPendingConnector="currentPendingConnector"
                :class="{ 'mt-12': !isFullWidthAuth }"
            ></component>
            <span v-if="activeComponent && !isFullWidthAuth" class="support-info">
                Если у вас возникли вопросы - напишите в нашу техподдержку:
                <a href="https://t.me/dataheroes_bot">https://t.me/dataheroes_bot</a>
            </span>
        </div>
    </div>
</template>

<script>
import YClientsAuth from '@/components/connectors/yClients/Auth.vue'
import YClientsSimpleAuth from '@/components/connectors/yClients/AuthSimple.vue'
import UniverseSoftAuth from '@/components/connectors/universeSoft/Auth.vue'
import SycretAuth from '@/components/connectors/sycret/Auth.vue'
import SonlineAuth from '@/components/connectors/sonline/Auth.vue'
import ArnicaAuth from '@/components/connectors/arnicaV2/ArnicaV2.vue'
import AltegioAuth from '@/components/connectors/altegio/Auth.vue'
import ProfSalonBatAuth from '@/components/connectors/profSalonBat/Auth.vue'
import ArchimedAuth from '@/components/connectors/archimed/Auth.vue'
import dentalProAuth from '@/components/connectors/dentalPro/Auth.vue'
import KlientixAuth from '@/components/connectors/klientix/Auth.vue'
import Cloud1CAuth from '@/components/connectors/cloud1C/Auth.vue'
import OnBoarding from '@/components/project/onBoarding/OnBoarding.vue'
import DikidiAuth from '@/components/connectors/dikidi/AuthSimple.vue'
import WaitThaiAuth from '@/components/connectors/waitThai/Auth.vue'
import OtherConnector from './OtherConnector'
import { CONNECTOR_TYPES } from '@/vars/general'
import { computed, getCurrentInstance, onBeforeMount, onBeforeUnmount, ref } from 'vue'
import { useProject } from '@root/src/hooks/useProject'
import api from '../../../api/api'
import { getInstance } from '@/auth/authWrapper'
import { createPendingConnectorRecord, savePendingComment } from '@/api/pendingConnectors'
import { PENDING_CONNECTOR_TYPES } from '../../../vars/general'
// Коннекторы, которые создаёт сам компонент авторизации (project/addConnector не нужен)
const SERVER_CREATED_CONNECTOR_TYPES = [CONNECTOR_TYPES.waitThai]

export default {
    name: 'AuthorizationInCRMSystem',

    components: {
        OnBoarding,
        YClientsAuth,
        YClientsSimpleAuth,
        UniverseSoftAuth,
        SycretAuth,
        SonlineAuth,
        ArnicaAuth,
        AltegioAuth,
        ProfSalonBatAuth,
        ArchimedAuth,
        dentalProAuth,
        KlientixAuth,
        Cloud1CAuth,
        DikidiAuth,
        WaitThaiAuth,
        OtherConnector
    },

    setup(props, { emit }) {
        const root = getCurrentInstance().proxy
        const store = root.$store
        const { project } = useProject()
        const currentPendingConnector = ref();

        const views = [
            { connectorType: CONNECTOR_TYPES.yClients, component: YClientsSimpleAuth },
            { connectorType: CONNECTOR_TYPES.universeSoft, component: UniverseSoftAuth },
            { connectorType: CONNECTOR_TYPES.sycret, component: SycretAuth },
            { connectorType: CONNECTOR_TYPES.profSalon, component: ProfSalonBatAuth },
            { connectorType: CONNECTOR_TYPES.profSalonBat, component: ProfSalonBatAuth },
            { connectorType: CONNECTOR_TYPES.profSalonBatV2, component: ProfSalonBatAuth },
            { connectorType: CONNECTOR_TYPES.sonline, component: SonlineAuth },
            { connectorType: CONNECTOR_TYPES.arnicaV2, component: ArnicaAuth },
            { connectorType: CONNECTOR_TYPES.altegio, component: AltegioAuth },
            { connectorType: CONNECTOR_TYPES.archimed, component: ArchimedAuth },
            { connectorType: CONNECTOR_TYPES.cloud1C, component: Cloud1CAuth },
            { connectorType: CONNECTOR_TYPES.dentalPro, component: dentalProAuth },
            { connectorType: CONNECTOR_TYPES.klientix, component: KlientixAuth },
            { connectorType: CONNECTOR_TYPES.dikidi, component: DikidiAuth },
            { connectorType: CONNECTOR_TYPES.waitThai, component: WaitThaiAuth },
            { connectorType: 'other', component: OtherConnector },
        ]

        const normalizeConnectorType = (value) => {
            const type = String(value ?? '').replace(/"/g, '')
            if (!type || type === 'null' || type === 'undefined') return ''
            return type
        }

        const onboardingProjectId = () =>
            project.value?.id || root.$router.currentRoute.params.id

        const isKnownOnboardingCrm = (type) =>
            views.some((el) => el.connectorType === type)

        const typeFromSession = () =>
            normalizeConnectorType(sessionStorage.getItem('currentAddingConnector'))

        const typeFromLocalStorage = () => {
            try {
                const projectId = onboardingProjectId()
                if (!projectId) return ''
                const raw = window.localStorage.getItem(`${projectId}-onboardingSelectedCrm`)
                if (!raw) return ''
                const parsed = JSON.parse(raw)
                return normalizeConnectorType(parsed?.type)
            } catch (e) {
                return ''
            }
        }

        const currentAddingConnectorType = () => {
            const fromSession = typeFromSession()
            if (isKnownOnboardingCrm(fromSession)) return fromSession
            const fromLocal = typeFromLocalStorage()
            if (isKnownOnboardingCrm(fromLocal)) return fromLocal
            return ''
        }

        const isFullWidthAuth = computed(() => {
            const t = currentAddingConnectorType()
            return (
                t === 'other' ||
                t === CONNECTOR_TYPES.cloud1C ||
                t === CONNECTOR_TYPES.profSalon ||
                t === CONNECTOR_TYPES.profSalonBat ||
                t === CONNECTOR_TYPES.profSalonBatV2 ||
                t === CONNECTOR_TYPES.universeSoft ||
                t === CONNECTOR_TYPES.dentalPro ||
                t === CONNECTOR_TYPES.archimed ||
                t === CONNECTOR_TYPES.sycret ||
                t === CONNECTOR_TYPES.altegio ||
                t === CONNECTOR_TYPES.arnicaV2 ||
                t === CONNECTOR_TYPES.dikidi ||
                t === CONNECTOR_TYPES.waitThai ||
                t === CONNECTOR_TYPES.klientix ||
                t === CONNECTOR_TYPES.yClients
            )
        })

        const isOtherConnector = computed(() => currentAddingConnectorType() === 'other')

        const activeComponent = computed(() => {
            const currentConnectorType = currentAddingConnectorType()
            return views.find((el) => el.connectorType === currentConnectorType)?.component
        })

        const next = async () => {
            if (currentAddingConnectorType() === 'other') {
                // Финальный экран и выход обрабатывает OtherConnector
                return
            }

            const connType = project.value.connectors[0].connectorType === CONNECTOR_TYPES.yClients ?
                'whatsappBasis' :
                'whatsapp'
            store.commit('project/updateCurrentAddingConnector', connType)
            if (currentPendingConnector.value) {
                await savePendingComment({id: currentPendingConnector.value.connectorId, comment: ''})
            }
            localStorage.removeItem(project.value.id + '-onboardingSelectedCrm')
            emit('refreshStep')
        }

        const goBack = () => {
            const projectId = onboardingProjectId()
            if (projectId) {
                localStorage.removeItem(projectId + '-onboardingSelectedCrm')
            }
            sessionStorage.removeItem('currentAddingConnector')
            store.commit('project/updateCurrentAddingConnector', null)
            emit('refreshStep')
        }

        const login = async () => {
            const loggedAddingConnector = ref(JSON.parse(sessionStorage.getItem('loggedAddingConnector')))
            // WaitThai уже создан и сохранён на бэке в /waitThai/connect (и добавлен в store)
            if (!SERVER_CREATED_CONNECTOR_TYPES.includes(loggedAddingConnector.value.connectorType)) {
                const res = await root.$store.dispatch('project/addConnector', loggedAddingConnector.value)
                if (res.error) {
                    root.$store.dispatch('callNotify', 'Ошибка')
                    return
                }
            }
            const connectorId = loggedAddingConnector.value.connectorId
            const auth = getInstance()
            if (PENDING_CONNECTOR_TYPES.includes(loggedAddingConnector.value.connectorType)) {
                await createPendingConnectorRecord({
                    userName: auth.user.name,
                    userEmail: auth.user.email,
                    projectId: project.value.id,
                    projectName: project.value.name,
                    connectorId: connectorId,
                    userId: auth.user.sub,
                    connectorType: loggedAddingConnector.value.connectorType,
                    webhookKey: null,
                    stage: 1,
                })
            }

            // WaitThai: настроечного шага нет, черновик снять было бы некому (07 §1: никакого isDraft)
            if (!SERVER_CREATED_CONNECTOR_TYPES.includes(loggedAddingConnector.value.connectorType)) {
                await root.$store.dispatch('project/updateConnectorSettings', {
                    connectorId,
                    settings: {
                        isDraft: true,
                    },
                })
            }
            next()
        }

        root.$on('login', login)
        root.$on('next', next)
        root.$on('goBackToCrm', goBack)
        onBeforeMount( async () => {
            const type = currentAddingConnectorType()
            if (!type) {
                goBack()
                return
            }
            if (typeFromSession() !== type) {
                store.commit('project/updateCurrentAddingConnector', type)
            }
            try {
                const pendingConnectors = await api.project.getPendingConnectorsByProject({
                    projectId: project.value?.id,
                })
                currentPendingConnector.value = Array.isArray(pendingConnectors?.data)
                    ? pendingConnectors.data[0]
                    : null
            } catch (e) {
                currentPendingConnector.value = null
            }
          }
        )
        onBeforeUnmount(() => {
            root.$off('login', login)
            root.$off('next', next)
            root.$off('goBackToCrm', goBack)
        })
        return {
            activeComponent,
            currentPendingConnector,
            isOtherConnector,
            isFullWidthAuth,
            goBack
        }
    },
}
</script>
<style lang="sass" scoped>
@import '@/vars.sass'

.content-wrapper
    margin-top: 0
    width: 100%
    max-width: 100%
    min-width: 0
    box-sizing: border-box
    .content
        align-items: stretch
        justify-content: center
        display: flex
        flex-direction: column
        width: 100%
        max-width: 100%
        min-width: 0
        padding-inline: 0
        box-sizing: border-box
        &.content--other
            max-width: 100%
            width: 100%
            align-items: stretch
        .support-info
            margin-top: 20px
            font-size: 18px
            font-weight: 600
    .to-crm-select-btn
        align-self: flex-start

@media (max-width: 700px)
    .content-wrapper .content .support-info
        font-size: 14px
</style>
